import { createAdapter, type ChessAdapter } from '@domain/chess/adapter';
import { historyFromSans } from '@domain/chess/moveTurns';
import type { Move, Role, Square } from '@domain/chess/types';
import { START_FEN } from '@domain/chess/types';
import { onCheckmate, onPositionChanged } from '@engine/engineService';
import { gameStore, setGameStore, type GameState } from './gameStore';

function notifyEngine(): void {
  if (adapter.isCheckmate()) {
    const mated = adapter.snapshot().turn;
    onCheckmate(mated === 'w' ? 'b' : 'w');
    return;
  }
  onPositionChanged(gameStore.fen);
}

/**
 * All product mutations go through actions.
 * UI → actions → store / adapter / engine bridge.
 */
let adapter: ChessAdapter = createAdapter();

export function getAdapter(): ChessAdapter {
  return adapter;
}

export function setAdapter(next: ChessAdapter): void {
  adapter = next;
}

function rebuildToCursor(cursor: number): void {
  const { startFen, history } = gameStore;
  adapter.reset(startFen);
  const clamped = Math.max(0, Math.min(cursor, history.length));
  for (let i = 0; i < clamped; i++) {
    const m = history[i]!;
    adapter.move({ from: m.from, to: m.to, promotion: m.promotion });
  }
  const last = clamped > 0 ? history[clamped - 1]! : null;
  setGameStore({
    fen: adapter.fen(),
    cursor: clamped,
    selected: null,
    legalTargets: [],
    pendingPromotion: null,
    lastMove: last ? { from: last.from, to: last.to } : null,
  });
  notifyEngine();
}

export function newGame(fen: string = START_FEN): void {
  adapter.reset(fen);
  setGameStore({
    startFen: fen,
    fen: adapter.fen(),
    history: [],
    cursor: 0,
    selected: null,
    legalTargets: [],
    pendingPromotion: null,
    lastMove: null,
    loadedOpeningName: null,
    revealCorrectSquares: [],
    interaction: 'play',
    puzzlePieces: [],
    markedSquares: [],
    markLog: [],
    revealMarks: [],
    engine: {
      ...gameStore.engine,
      score: undefined,
      bestMoveUci: undefined,
      currentRequestId: null,
      status: gameStore.engine.status === 'thinking' ? 'ready' : gameStore.engine.status,
    },
  });
  notifyEngine();
}

/**
 * Soft-load a FEN (URL hash / paste). Returns false if invalid; does not throw.
 * Resets history to a single root position at that FEN.
 * No-ops (still true) if adapter is already on that position — avoids wiping selection.
 */
export function tryLoadFen(
  fen: string,
  opts?: { orientation?: 'white' | 'black' },
): boolean {
  const trimmed = fen.trim().replace(/\s+/g, ' ');
  if (!trimmed) return false;

  const applyOrientation = (): void => {
    if (opts?.orientation) setGameStore('orientation', opts.orientation);
  };

  // Already at this position (string match or adapter already loaded)
  if (trimmed === gameStore.fen.replace(/\s+/g, ' ') && gameStore.history.length === 0) {
    applyOrientation();
    return true;
  }

  const ok = adapter.tryLoadFen(trimmed);
  if (!ok) return false;
  const loaded = adapter.fen();

  // Same position after chess.js normalization — don't clobber UI if only whitespace differed
  if (loaded === gameStore.fen && gameStore.startFen === loaded && gameStore.history.length === 0) {
    applyOrientation();
    return true;
  }

  setGameStore({
    startFen: loaded,
    fen: loaded,
    history: [],
    cursor: 0,
    selected: null,
    legalTargets: [],
    pendingPromotion: null,
    lastMove: null,
    ...(opts?.orientation ? { orientation: opts.orientation } : {}),
  });
  notifyEngine();
  return true;
}


export function beginMarkPuzzle(
  pieces: GameState['puzzlePieces'],
): void {
  setGameStore({
    interaction: 'mark',
    puzzlePieces: pieces,
    markedSquares: [],
    markLog: [],
    revealMarks: [],
    selected: null,
    legalTargets: [],
    loadedOpeningName: null,
    revealCorrectSquares: [],
  });
}

export function endMarkPuzzle(): void {
  setGameStore({
    interaction: 'play',
    puzzlePieces: [],
    markedSquares: [],
    markLog: [],
    revealMarks: [],
  });
  newGame();
}

export function toggleMarkedSquare(sq: Square): void {
  if (gameStore.interaction !== 'mark') return;
  if (gameStore.revealMarks.length > 0) return;
  const has = gameStore.markedSquares.includes(sq);
  const markedSquares = has
    ? gameStore.markedSquares.filter((s) => s !== sq)
    : [...gameStore.markedSquares, sq];
  setGameStore({
    markedSquares,
    markLog: [...gameStore.markLog, sq],
  });
}

export function undoMark(): void {
  if (gameStore.interaction !== 'mark') return;
  if (gameStore.revealMarks.length > 0) return;
  const log = gameStore.markLog;
  if (log.length === 0) return;
  const sq = log[log.length - 1]!;
  const has = gameStore.markedSquares.includes(sq);
  const markedSquares = has
    ? gameStore.markedSquares.filter((s) => s !== sq)
    : [...gameStore.markedSquares, sq];
  setGameStore({
    markedSquares,
    markLog: log.slice(0, -1),
  });
}

export function setRevealMarks(
  marks: GameState['revealMarks'],
): void {
  setGameStore('revealMarks', marks);
}

export function selectSquare(sq: Square | null): void {
  if (gameStore.interaction === 'mark') {
    if (sq) toggleMarkedSquare(sq);
    return;
  }
  if (!sq) {
    setGameStore({ selected: null, legalTargets: [] });
    return;
  }

  const { selected, legalTargets, pendingPromotion } = gameStore;
  if (pendingPromotion) return;

  // Click target of a legal move (cached targets)
  if (selected && legalTargets.includes(sq)) {
    tryMove(selected, sq);
    return;
  }

  // Drop / click destination: re-check legality even if cache was cleared
  if (selected && selected !== sq) {
    const legal = adapter.moves(selected).some((m) => m.to === sq);
    if (legal) {
      tryMove(selected, sq);
      return;
    }
  }

  // Select own piece
  const snap = adapter.snapshot();
  const piece = snap.pieces.get(sq);
  if (!piece || piece.color !== snap.turn) {
    setGameStore({ selected: null, legalTargets: [] });
    return;
  }

  const targets = adapter.moves(sq).map((m) => m.to);
  setGameStore({ selected: sq, legalTargets: targets });
}

/** Direct move for drag-drop (does not require legalTargets cache). */
export function attemptMove(from: Square, to: Square): void {
  if (gameStore.interaction === 'mark') return;
  if (gameStore.pendingPromotion) return;
  if (from === to) {
    setGameStore({ selected: null, legalTargets: [] });
    return;
  }
  const legal = adapter.moves(from).some((m) => m.to === to);
  if (!legal) {
    // try select destination piece instead
    selectSquare(to);
    return;
  }
  tryMove(from, to);
}

function needsPromotion(from: Square, to: Square): boolean {
  const moves = adapter.moves(from).filter((m) => m.to === to);
  return moves.some((m) => m.promotion);
}

function tryMove(from: Square, to: Square, promotion?: Role): void {
  if (needsPromotion(from, to) && !promotion) {
    setGameStore({
      pendingPromotion: { from, to },
      selected: from,
      legalTargets: [],
    });
    return;
  }

  // Truncate future if scrubbed
  const baseHistory =
    gameStore.cursor < gameStore.history.length
      ? gameStore.history.slice(0, gameStore.cursor)
      : gameStore.history;

  // Rebuild to tip of base, then move
  adapter.reset(gameStore.startFen);
  for (const m of baseHistory) {
    adapter.move({ from: m.from, to: m.to, promotion: m.promotion });
  }

  const result = adapter.move({ from, to, promotion });
  if (!result) {
    setGameStore({ selected: null, legalTargets: [], pendingPromotion: null });
    return;
  }

  const history: Move[] = [...baseHistory, result];
  setGameStore({
    fen: adapter.fen(),
    history,
    cursor: history.length,
    selected: null,
    legalTargets: [],
    pendingPromotion: null,
    lastMove: { from: result.from, to: result.to },
  });
  notifyEngine();
}

export function confirmPromotion(role: Role): void {
  const pending = gameStore.pendingPromotion;
  if (!pending) return;
  tryMove(pending.from, pending.to, role);
}

export function cancelPromotion(): void {
  setGameStore({ pendingPromotion: null, selected: null, legalTargets: [] });
}

export function setCursor(i: number): void {
  rebuildToCursor(i);
}

export function undo(): void {
  if (gameStore.cursor <= 0) return;
  setCursor(gameStore.cursor - 1);
}

export function redo(): void {
  if (gameStore.cursor >= gameStore.history.length) return;
  setCursor(gameStore.cursor + 1);
}

export function toggleCreatorMode(): void {
  setGameStore('ui', 'creatorMode', !gameStore.ui.creatorMode);
}

export function setOrientation(orientation: 'white' | 'black'): void {
  setGameStore('orientation', orientation);
}

export function setLoadedOpening(name: string | null): void {
  setGameStore('loadedOpeningName', name);
}

export function setRevealCorrectSquares(squares: Square[]): void {
  setGameStore('revealCorrectSquares', squares);
}

/**
 * Replay a one-move tactic from its before-FEN so undo and the eval bar stay
 * attached to the solution position. Does not wipe an existing engine score;
 * analysis restarts only if the after-position is new.
 */
export function loadTacticSolution(opts: {
  startFen: string;
  from: Square;
  to: Square;
  name: string;
  promotion?: Role;
}): boolean {
  adapter.reset(opts.startFen);
  const result = adapter.move({
    from: opts.from,
    to: opts.to,
    promotion: opts.promotion,
  });
  if (!result) {
    adapter.reset(opts.startFen);
    return false;
  }
  setGameStore({
    startFen: opts.startFen,
    fen: adapter.fen(),
    history: [result],
    cursor: 1,
    selected: null,
    legalTargets: [],
    pendingPromotion: null,
    lastMove: { from: result.from, to: result.to },
    loadedOpeningName: opts.name,
    revealCorrectSquares: [],
    interaction: 'play',
    puzzlePieces: [],
    markedSquares: [],
    markLog: [],
    revealMarks: [],
  });
  notifyEngine();
  return true;
}

/** Load a catalog opening from its SAN line so turn numbers (and undo) work. */
export function loadOpening(opening: {
  name: string;
  fen: string;
  moves: string;
  side: 'white' | 'black';
}): boolean {
  const orientation = opening.side === 'black' ? 'black' : 'white';
  const line = historyFromSans(opening.moves);

  if (line && line.length > 0) {
    adapter.reset(START_FEN);
    const history: Move[] = [];
    let ok = true;
    for (const mv of line) {
      const result = adapter.move({
        from: mv.from,
        to: mv.to,
        promotion: mv.promotion,
      });
      if (!result) {
        ok = false;
        break;
      }
      history.push(result);
    }

    if (ok && history.length === line.length) {
      const last = history[history.length - 1]!;
      setGameStore({
        startFen: START_FEN,
        fen: adapter.fen(),
        history,
        cursor: history.length,
        selected: null,
        legalTargets: [],
        pendingPromotion: null,
        lastMove: { from: last.from, to: last.to },
        orientation,
        loadedOpeningName: opening.name,
        revealCorrectSquares: [],
      });
      notifyEngine();
      return true;
    }
  }

  const loaded = tryLoadFen(opening.fen, { orientation });
  setOrientation(orientation);
  setLoadedOpening(opening.name);
  setRevealCorrectSquares([]);
  return loaded;
}

export function flipBoard(): void {
  setGameStore(
    'orientation',
    gameStore.orientation === 'white' ? 'black' : 'white',
  );
}

export function snapshotPieces() {
  return adapter.snapshot();
}

export function gameOutcome(): 'playing' | 'checkmate' | 'draw' {
  if (adapter.isCheckmate()) return 'checkmate';
  if (adapter.isDraw()) return 'draw';
  return 'playing';
}

